import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import customBaseQuery from "./baseQuery";

// Response types
interface ColorResponse {
  data: Array<{
    id: number;
    name: string;
    color_code: string;
    is_active: boolean;
  }>;
}

interface LocationResponse {
  data: Array<{
    id: number;
    name: string;
    is_delete: boolean;
  }>;
}

interface ColorDetailsResponse {
  data: {
    color: {
      id: number;
      name: string;
      code: string;
      is_active: boolean;
    };
  };
}

interface CreateColorRequest {
  name: string;
  code: string;
}

interface UpdateColorRequest {
  fancyColorId: number;
  colorCode: string;
}

interface UpdateLocationRequest {
  locationId: number;
  isDisable: boolean;
}

export const settingsApi = createApi({
  reducerPath: "settingsApi",
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    // Fancy Colors endpoints
    getFancyColorsList: builder.query<ColorResponse, number>({
      query: (page) => ({
        url: `/settings/fancy-colors?page=${page}`,
        method: "GET",
      }),
    }),
    updateFancyColor: builder.mutation<void, UpdateColorRequest>({
      query: (body) => ({
        url: "/settings/fancy-colors",
        method: "PUT",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("token")?.toString() || "",
        },
      }),
    }),

    // Location endpoints
    getLocationsList: builder.query<LocationResponse, number>({
      query: (page) => ({
        url: `/settings/locations?page=${page}`,
        method: "GET",
      }),
    }),
    updateLocationStatus: builder.mutation<void, UpdateLocationRequest>({
      query: (body) => ({
        url: "/settings/locations",
        method: "PUT",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("token")?.toString() || "",
        },
      }),
      async onQueryStarted(
        { locationId, isDisable },
        { queryFulfilled, dispatch }
      ) {
        try {
          await queryFulfilled;
          // Update the cache optimistically
          dispatch(
            settingsApi.util.updateQueryData("getLocationsList", 1, (draft) => {
              if (draft?.data) {
                const location = draft.data.find(
                  (loc) => loc.id === locationId
                );
                if (location) {
                  location.is_delete = isDisable;
                }
              }
            })
          );
        } catch {
          // If the mutation fails, the cache will be automatically reverted
        }
      },
    }),
  }),
});

export const {
  useGetFancyColorsListQuery,
  useUpdateFancyColorMutation,
  useGetLocationsListQuery,
  useUpdateLocationStatusMutation,
} = settingsApi;
